<div class="mt-8">
  <div class="flex items-center justify-between mb-6 flex-wrap gap-4">
    <h2 class="text-foreground text-xl font-bold m-0">Assessment History</h2>

    <!-- Date Range Picker -->
    <p-calendar
      [(ngModel)]="dateRange"
      selectionMode="range"
      [readonlyInput]="true"
      [showClear]="true"
      placeholder="Select Date Range"
      (onSelect)="applyDateFilter()"
      (onClear)="applyDateFilter()"
    >
    </p-calendar>
  </div>

  <div class="flex flex-col gap-4">
    <!-- Empty State -->
    <div
      *ngIf="filteredHistory.length === 0"
      class="flex flex-col items-center justify-center p-12 bg-gray-50 dark:bg-card/50 rounded-[24px] border border-dashed border-gray-200 dark:border-foreground-tertiary"
    >
      <i class="pi pi-calendar-times text-4xl text-gray-300 mb-3"></i>
      <h3 class="text-gray-600 dark:text-gray-400 font-medium">
        No assessment history found for this date range.
      </h3>
    </div>

    <div
      *ngFor="let item of filteredHistory"
      class="bg-card rounded-2xl p-4 border border-gray-100 dark:border-foreground-tertiary shadow-sm flex items-center gap-4 cursor-pointer hover:shadow-md transition-shadow"
      (click)="openDetails(item)"
    >
      <!-- Icon -->
      <div
        class="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center flex-shrink-0"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#10b981"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path
            d="M20.42 4.58a5.4 5.4 0 0 0-7.65 0l-.77.78-.77-.78a5.4 5.4 0 0 0-7.65 0C1.46 6.7 1.33 10.28 4 13l8 8 8-8c2.67-2.72 2.54-6.3.42-8.42z"
          ></path>
          <polyline points="10 12 12 10 14 14 16 10"></polyline>
        </svg>
      </div>

      <!-- Summary Info -->
      <div class="flex-1 min-w-0">
        <div class="flex items-center gap-3">
          <span
            class="text-[16px] font-semibold text-foreground border-r border-gray-200 pr-3 dark:text-gray-100 dark:border-gray-700"
          >
            {{ item.value }} / 100
          </span>

          <div
            class="flex items-center gap-1 text-[11px] font-medium"
            [ngClass]="
              item.progress_from_last_assessment === 0
                ? 'text-gray-500'
                : item.is_positive
                  ? 'text-green-600'
                  : 'text-red-500'
            "
          >
            <svg
              *ngIf="item.progress_from_last_assessment !== 0 && item.is_positive"
              xmlns="http://www.w3.org/2000/svg"
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="3"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
              <polyline points="16 7 22 7 22 13"></polyline>
            </svg>

            <svg
              *ngIf="item.progress_from_last_assessment !== 0 && !item.is_positive"
              xmlns="http://www.w3.org/2000/svg"
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="3"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="22 17 13.5 8.5 8.5 13.5 2 7"></polyline>
              <polyline points="16 17 22 17 22 11"></polyline>
            </svg>

            <span *ngIf="item.progress_from_last_assessment !== 0"
              >{{ item.is_positive ? '+' : '-' }}{{ item.progress_from_last_assessment }}%</span
            >
            <span *ngIf="item.progress_from_last_assessment === 0">0%</span>
            <span class="text-gray-400 font-normal ml-0.5 whitespace-nowrap hidden sm:inline"
              >vs last assessment</span
            >
          </div>
        </div>
        <p class="text-[12px] text-gray-500 mt-1">Generated on {{ item.date }}</p>
        <p class="text-[11px] text-gray-600 mt-2 line-clamp-1 leading-snug">
          {{ getHintText(item) }}
        </p>
      </div>

      <!-- Chevron -->
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        class="text-gray-400 stroke-gray-400"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <polyline points="9 18 15 12 9 6"></polyline>
      </svg>
    </div>
  </div>
</div>

<!-- Modal Dialog -->
<p-dialog
  [(visible)]="displayModal"
  [modal]="true"
  header="Assessment Score Details"
  [draggable]="false"
  [resizable]="false"
  closeIcon="pi pi-times"
  [showHeader]="true"
  [dismissableMask]="true"
  styleClass="rounded-3xl overflow-hidden max-w-[440px] w-[95vw] shadow-xl dark:border-foreground-tertiary m-0"
>
  <div *ngIf="selectedAssessment" class="p-2 relative rounded-2xl">
    <!-- Custom Header inside body to match UI perfectly -->
    <!-- <div class="flex items-center justify-center mb-5 pb-2">

          <h2 class="text-lg font-bold text-gray-900 mt-1 dark:text-white">Assessment Score Details</h2>
      </div> -->

    <!-- Main Score Section -->
    <div class="flex items-start justify-between mb-4">
      <div class="text-[32px] font-bold text-foreground leading-none">
        {{ selectedAssessment.value }}
        <span class="text-[24px] font-semibold text-muted-color">/ 100</span>
      </div>
      <span
        class="px-3 py-1.5 rounded-md text-sm font-medium tracking-wide bg-[#FFF8ED] text-[#F5A623] border border-[#ffedd5]"
      >
        {{ getLabel(selectedAssessment.value) }}
      </span>
    </div>

    <!-- Dynamic Progress Status -->
    <div
      class="flex items-center gap-1.5 text-[14px] font-medium mb-1"
      [ngClass]="
        selectedAssessment.progress_from_last_assessment === 0
          ? 'text-gray-500'
          : selectedAssessment.is_positive
            ? 'text-emerald-600'
            : 'text-rose-500'
      "
    >
      <svg
        *ngIf="
          selectedAssessment.progress_from_last_assessment !== 0 && selectedAssessment.is_positive
        "
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
        <polyline points="16 7 22 7 22 13"></polyline>
      </svg>
      <svg
        *ngIf="
          selectedAssessment.progress_from_last_assessment !== 0 && !selectedAssessment.is_positive
        "
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <polyline points="22 17 13.5 8.5 8.5 13.5 2 7"></polyline>
        <polyline points="16 17 22 17 22 11"></polyline>
      </svg>

      <span *ngIf="selectedAssessment.progress_from_last_assessment !== 0"
        >{{ selectedAssessment.is_positive ? '+' : '-'
        }}{{ selectedAssessment.progress_from_last_assessment }}%</span
      >
      <span *ngIf="selectedAssessment.progress_from_last_assessment === 0">0%</span>
      <span class="text-foreground font-normal">vs last assessment</span>
    </div>

    <p class="text-[12px] text-gray-500 mb-3">
      Assessment recorded on {{ selectedAssessment.date }}
    </p>

    <hr class="border-t-[2px] border-dashed border-gray-200 mb-3 dark:border-gray-700" />

    <!-- Historical Comparison -->
    <h3 class="text-[14px] font-bold text-foreground mb-4 dark:text-gray-200">
      Change From Previous Assessment
    </h3>

    <div class="space-y-4 mb-6">
      <div class="flex items-center justify-between text-[14px]">
        <div class="flex items-center gap-3 text-muted-color font-medium dark:text-gray-300">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="text-gray-500"
          >
            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
          </svg>
          Previous Score:
        </div>
        <span class="font-bold text-gray-900 dark:text-white">{{ previousScore }} / 100</span>
      </div>

      <div class="flex items-center justify-between text-[14px]">
        <div class="flex items-center gap-3 text-muted-color font-medium dark:text-gray-300">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="text-gray-500"
          >
            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
          </svg>
          Current Score:
        </div>
        <span class="font-bold text-gray-900 dark:text-white"
          >{{ selectedAssessment.value }} / 100</span
        >
      </div>
    </div>

    <!-- Info Alert Box -->
    <div class="bg-[#FFF8ED] border border-[#ffedd5] rounded-xl p-4 flex gap-3 mb-8">
      <div
        class="w-5 h-5 rounded-full bg-[#F5A623] text-white flex items-center justify-center flex-shrink-0 mt-0.5"
      >
        <span class="text-[12px] font-bold italic font-serif">i</span>
      </div>
      <p class="text-[13px] text-gray-700 leading-relaxed font-medium">
        {{ getModalAlertText(selectedAssessment) }}
      </p>
    </div>

    <!-- Call to action -->
    <button
      class="w-full bg-[#F5A623] hover:bg-[#e69819] transition-colors text-white font-bold text-[15px] py-3.5 rounded-full flex items-center justify-center gap-2"
      (click)="goToAssessmentScore()"
    >
      View Detailed Summary
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <line x1="5" y1="12" x2="19" y2="12"></line>
        <polyline points="12 5 19 12 12 19"></polyline>
      </svg>
    </button>
  </div>
</p-dialog>
